/**
 * @file create campaign config
 * @author caiguoqing
 */
import {
  AwarenessIcon,
  WebsiteTrafficIcon,
  InstallsIcon,
  SignUpsIcon,
  LeadsIcon,
  SalesIcon,
} from "@/components/icons";
import capitalizeFirstLetter from "@/utils/capitalizeFirstLetter";
import { Tooltip } from "antd";
import { getPlatformName } from "@/biz/platform";

// 这个枚举一定要按照创建步骤顺序写，要不然会影响每一步上面的展示
export enum CreateCampaignStageEnum {
  BUSINESS_INFORMATION = "key_message",
  SETTINGS = "setting",
  BUDGET_BIDDING = "budget",
  PREVIEW_SAMPLE_MATCHES = "preview_sample_matches",
}

export const stageNextTextMap: any = {
  [CreateCampaignStageEnum.BUSINESS_INFORMATION]: "Next",
  [CreateCampaignStageEnum.SETTINGS]: "Next",
  [CreateCampaignStageEnum.PREVIEW_SAMPLE_MATCHES]: "Next",
  [CreateCampaignStageEnum.BUDGET_BIDDING]: "Release",
};

export enum AdsCampaignMarketingStrategy {
  INFLUENCER_MARKETING = "influencer_marketing",
  AFFILIATES_MARKETING = "affiliates_marketing",
  SALES_OUTREACH = "sales_outreach",
}

export enum SelectProductTabEnum {
  Select = "Select a product",
  newProduct = "Create a new product",
}

export enum ProductType {
  WEB_APP = "web app",
  MOBILE_APP = "mobile app",
  DESKTOP_APP = "desktop app",
  BROWSER_EXTENSION = "browser extension",
  PHYSICAL_PRODUCT = "physical product",
}

export const clientTypeOptions = Object.entries(ProductType).map(
  ([label, value]) => {
    return {
      label: capitalizeFirstLetter(value),
      value,
    };
  },
);

export enum BusinessModelEnum {
  TO_BUSINESS = "to business",
  TO_CUSTOMER = "to customer",
}

export const businessLab: any = {
  [BusinessModelEnum.TO_BUSINESS]: "B2B",
  [BusinessModelEnum.TO_CUSTOMER]: "B2C",
};

export const businessModelOptions = [
  {
    label: businessLab[BusinessModelEnum.TO_CUSTOMER],
    value: BusinessModelEnum.TO_CUSTOMER,
  },
  {
    label: businessLab[BusinessModelEnum.TO_BUSINESS],
    value: BusinessModelEnum.TO_BUSINESS,
  },
];

export enum AdsCampaignObjective {
  AWARENESS = "Awareness",
  WEBSITE_TRAFFIC = "Website Traffic",
  INSTALLS = "Installs",
  LEADS = "Leads",
  SIGN_UPS = "Sign-ups",
  SALES = "Sales",
}

export enum AdsCampaignObjectiveSubType {
  SUBSCRIPTION = "Subscription",
  PAY_AS_YOU_GO = "Pay-as-You-Go",
  ONE_TIME_PAYMENT = "One-time payment",
}

export const targetFormLab: any = {
  [AdsCampaignObjective.INSTALLS]: {
    title: "Your click-to-install conversion rate",
    desc: "You can enter either your current conversion rate or your target conversion rate here to estimate your campaign’s result.",
    key: ["conversionIndicator", "clickToInstall"],
    tips: "Industry benchmark: 60%-80%.",
    message: "please enter click-to-install conversion rate",
  },
  [AdsCampaignObjective.SIGN_UPS]: {
    title: "Your click-to-sign-up conversion rate",
    desc: "You can enter either your current conversion rate or your target conversion rate here to estimate your campaign’s result.",
    key: ["conversionIndicator", "clickToSignup"],
    tips: "Industry benchmark: 20%-50%.",
    message: "please enter click-to-sign-up conversion rate",
  },
  [AdsCampaignObjective.LEADS]: {
    title: "Your click-to-lead conversion rate",
    desc: "You can enter either your current conversion rate or your target conversion rate here to estimate your campaign’s result.",
    key: ["conversionIndicator", "clickToLead"],
    tips: "Industry benchmark: 1%-5%.",
    message: "please enter click-to-lead conversion rate",
  },
};

export const targetOptions = [
  // {
  //   Icon: AwarenessIcon,
  //   title: 'Awareness',
  //   desc: 'Increase brand visibility and reach a broader audience to establish your presence in the market.',
  //   disabled: false,
  //   value: AdsCampaignObjective.AWARENESS
  // },
  {
    Icon: WebsiteTrafficIcon,
    title: "Traffic",
    desc: `Direct the right audience to your destinations, such as your website, app, social media pages, and more. Link clicks (PV) will be tracked.`,
    disabled: false,
    value: AdsCampaignObjective.WEBSITE_TRAFFIC,
  },
  {
    Icon: LeadsIcon,
    title: "Leads",
    desc: "Collect leads for your business or brand. Encourage customers to take action.",
    disabled: false,
    value: AdsCampaignObjective.LEADS,
  },
  // {
  //   Icon: InstallsIcon,
  //   title: 'App promotion',
  //   desc: 'Get more people to install, register your app, and continue using it.',
  //   disabled: true,
  //   value: AdsCampaignObjective.INSTALLS
  // },
  {
    Icon: SignUpsIcon,
    title: "Sign-Ups",
    desc: "Encourage users to register or create accounts, fostering deeper engagement with your platform.",
    disabled: false,
    value: AdsCampaignObjective.SIGN_UPS,
  },
  {
    Icon: SalesIcon,
    title: "Sales",
    desc: "Find people who are likely to purchase your product or service.",
    disabled: false,
    value: AdsCampaignObjective.SALES,
  },
];

export const salesOptions = [
  {
    title: "Subscription",
    desc: "Your product earns recurring revenue through subscriptions, with customers paying regularly (monthly, yearly, etc.) for access.",
    value: AdsCampaignObjectiveSubType.SUBSCRIPTION,
    disabled: false,
  },
  // {
  //   title: 'Pay as you go',
  //   desc: 'Your revenue is generated through usage-based or transaction-driven pricing, where users pay per their consumption (e.g., pay per click/query). ',
  //   value: AdsCampaignObjectiveSubType.PAY_AS_YOU_GO,
  //   disabled: true
  // },
  {
    title: "One-time payment",
    desc: "Your product depends on one-time purchases, so we'll focus on boosting quick conversions and single-sale revenue.",
    value: AdsCampaignObjectiveSubType.ONE_TIME_PAYMENT,
    disabled: false,
  },
];

export enum PlatFormEnum {
  TWITTER = "twitter",
  X = "x",
  YOUTUBE = "youtube",
  TIKTOK = "tiktok",
  INSTAGRAM = "instagram",
  LINKEDIN = "linkedin",
}

export const selectPlatformOPtions = [
  {
    value: PlatFormEnum.YOUTUBE,
    label: "YouTube",
  },
  {
    value: PlatFormEnum.INSTAGRAM,
    label: "Instagram",
  },
  {
    value: PlatFormEnum.TIKTOK,
    label: "TikTok",
  },
  {
    value: PlatFormEnum.TWITTER,
    label: "Twitter",
  },
  {
    value: PlatFormEnum.LINKEDIN,
    label: "LinkedIn",
  },
];

export enum PlatformContentEnum {
  YOUTUBE_DEDICATED = "youtubeDedicated",
  YOUTUBE_INTEGRATED = "youtubeIntegrated",
  TIKTOK_VIDEO = "tiktokVideo",
  // TIKTOK_INTEGRATED_VIDEO = 'tiktokIntegratedVideo',
  INSTAGRAM_REEL = "instagramReel",
  // INSTAGRAM_INTEGRATED_REEL = 'instagramIntegratedReel',
  INSTAGRAM_CAROUSEL_POST = "instagramCarouselPost",
  // INSTAGRAM_INTEGRATED_CAROUSEL_POST = 'instagramIntegratedCarouselPost',
  LINKEDIN_POST = "linkedinPost",
  LINKEDIN_IMAGE_POST = "linkedinImagePost",
  LINKEDIN_VIDEO_POST = "linkedinVideoPost",
  TWITTER_POST = "twitterPost",
  // FACEBOOK_POST = 'facebookPost',
  // FACEBOOK_REEL = 'facebookReel',
}

export enum PlatformContentLabelEnum {
  YOUTUBE_DEDICATED = "YouTube dedicated videos",
  YOUTUBE_INTEGRATED = "YouTube integrated videos",
  TIKTOK_VIDEO = "TikTok videos",
  TIKTOK_INTEGRATED_VIDEO = "TikTok integrated videos",
  INSTAGRAM_REEL = "Instagram Reels",
  INSTAGRAM_INTEGRATED_REEL = "Instagram integrated reels",
  INSTAGRAM_CAROUSEL_POST = "Instagram carousel posts",
  INSTAGRAM_INTEGRATED_CAROUSEL_POST = "Instagram integrated carousel posts",
  TWITTER_POST = "Twitter / X posts",
  LINKEDIN_IMAGE_POST = "LinkedIn image posts",
  LINKEDIN_VIDEO_POST = "LinkedIn video posts",
  LINKEDIN_POST = "LinkedIn posts",
  FACEBOOK_POST = "Facebook posts",
  FACEBOOK_REEL = "Facebook reels",
}

export const getPlatformContentLabelI18n = (key: string, t?: any) => {
  return {
    [PlatformContentEnum.YOUTUBE_DEDICATED]: t(
      "campaignCreate.platformContentLabel.youtubeDedicated",
    ),
    [PlatformContentEnum.YOUTUBE_INTEGRATED]: t(
      "campaignCreate.platformContentLabel.youtubeIntegrated",
    ),
    [PlatformContentEnum.TIKTOK_VIDEO]: t(
      "campaignCreate.platformContentLabel.tiktokVideo",
    ),
    [PlatformContentEnum.INSTAGRAM_REEL]: t(
      "campaignCreate.platformContentLabel.instagramReel",
    ),
    [PlatformContentEnum.INSTAGRAM_CAROUSEL_POST]: t(
      "campaignCreate.platformContentLabel.instagramCarouselPost",
    ),
    [PlatformContentEnum.TWITTER_POST]: t(
      "campaignCreate.platformContentLabel.twitterPost",
    ),
    [PlatformContentEnum.LINKEDIN_POST]: t(
      "campaignCreate.platformContentLabel.linkedinPost",
    ),
    [PlatformContentEnum.LINKEDIN_IMAGE_POST]: t(
      "campaignCreate.platformContentLabel.linkedinImagePost",
    ),
    [PlatformContentEnum.LINKEDIN_VIDEO_POST]: t(
      "campaignCreate.platformContentLabel.linkedinVideoPost",
    ),
  }[key];
};

export const getPlatformContentLabel = (key: string, t?: any) => {
  return {
    [PlatformContentEnum.YOUTUBE_DEDICATED]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.YOUTUBE_INTEGRATED]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.TIKTOK_VIDEO]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.INSTAGRAM_REEL]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.INSTAGRAM_CAROUSEL_POST]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.TWITTER_POST]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.LINKEDIN_POST]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.LINKEDIN_IMAGE_POST]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.LINKEDIN_VIDEO_POST]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    // [PlatformContentEnum.TIKTOK_INTEGRATED_VIDEO]: PlatformContentLabelEnum.TIKTOK_INTEGRATED_VIDEO,
    // [PlatformContentEnum.INSTAGRAM_INTEGRATED_REEL]: PlatformContentLabelEnum.INSTAGRAM_INTEGRATED_REEL,
    // [PlatformContentEnum.INSTAGRAM_INTEGRATED_CAROUSEL_POST]: PlatformContentLabelEnum.INSTAGRAM_INTEGRATED_CAROUSEL_POST,
    // [PlatformContentEnum.FACEBOOK_POST]: PlatformContentLabelEnum.FACEBOOK_POST,
    // [PlatformContentEnum.FACEBOOK_REEL]: PlatformContentLabelEnum.FACEBOOK_REEL,
  }[key];
};

export const getPlatformContentOptions = (t?: any, placementsValues?: any) =>
  Object.entries(PlatformContentEnum)
    .filter(
      ([key, value]) =>
        value !== PlatformContentEnum.LINKEDIN_POST ||
        (value === PlatformContentEnum.LINKEDIN_POST &&
          placementsValues?.includes(PlatformContentEnum.LINKEDIN_POST)),
    )
    ?.map(([key, value]) => ({
      value: value,
      label: (
        <Tooltip
          overlayInnerStyle={{ width: "488px" }}
          title={
            value === PlatformContentEnum.TWITTER_POST
              ? t("campaignCreate.settings.twitterPostTooltip")
              : ""
          }
        >
          <div style={{ width: "100%", display: "inline-block" }}>
            {
              PlatformContentLabelEnum[
                key as keyof typeof PlatformContentLabelEnum
              ]
            }
          </div>
        </Tooltip>
      ),
    }));

export enum LanguageEnum {
  CHINESE = "zh",
  ENGLISH = "en",
  HINDI = "hi",
  SPANISH = "es",
  ARABIC = "ar",
  FRENCH = "fr",
  BENGALI = "bn",
  RUSSIAN = "ru",
  PORTUGUESE = "pt",
  URDU = "ur",
  INDONESIAN = "id",
  GERMAN = "de",
  JAPANESE = "ja",
  VIETNAMESE = "vi",
  KOREAN = "ko",
  TAMIL = "ta",
  TURKISH = "tr",
  ITALIAN = "it",
  THAI = "th",
  PERSIAN = "fa",
  POLISH = "pl",
  UKRAINIAN = "uk",
  BURMESE = "my",
  SWAHILI = "sw",
  PASHTO = "ps",
  AMHARIC = "am",
  ROMANIAN = "ro",
  DUTCH = "nl",
  SINHALA = "si",
  SWEDISH = "sv",
  CZECH = "cs",
  HUNGARIAN = "hu",
  AZERBAIJANI = "az",
  GREEK = "el",
  HEBREW = "he",
  NEPALI = "ne",
  KHMER = "km",
  ZULU = "zu",
  SOMALI = "so",
  FINNISH = "fi",
  BULGARIAN = "bg",
  SLOVAK = "sk",
  DANISH = "da",
  CATALAN = "ca",
  CROATIAN = "hr",
  DZONGKHA = "dz",
  ESTONIAN = "et",
  GEORGIAN = "ka",
  ICELANDIC = "is",
  IRISH = "ga",
  KAZAKH = "kk",
  KURDISH = "ku",
  KYRGYZ = "ky",
  LAO = "lo",
  LATIN = "la",
  LATVIAN = "lv",
  LITHUANIAN = "lt",
  MACEDONIAN = "mk",
  MALAGASY = "mg",
  MALAY = "ms",
  MALTESE = "mt",
  MONGOLIAN = "mn",
  NORWEGIAN = "no",
  SERBIAN = "sr",
  SLOVENIAN = "sl",
  TAJIK = "tg",
  TURKMEN = "tk",
  UZBEK = "uz",
  XHOSA = "xh",
  FILIPINO = "fil",
  DIVEHI = "dv",
  BELARUSIAN = "be",
  ARMENIAN = "hy",
  TIGRINYA = "ti",
  KINYARWANDA = "rw",
  KIRUNDI = "rn",
  CAPE_VERDEAN_CREOLE = "crs",
  NYANJA = "ny",
  SWATI = "ss",
  COMORIAN = "swb",
  SOUTHERN_SOTHO = "st",
  HAITIAN_CREOLE = "ht",
  LUXEMBOURGISH = "lb",
  ALBANIAN = "sq",
  MONTENEGRIN = "cnr",
  FAROESE = "fo",
  BOSNIAN = "bs",
  PALAUAN = "pau",
  MARSHALLESE = "mh",
  GILBERTESE = "gil",
  NAURU = "na",
  BISLAMA = "bi",
  FIJIAN = "fj",
  TUVALUAN = "tvl",
  SAMOAN = "sm",
  TONGAN = "to",
  RAROTONGAN = "rar",
  NIUEAN = "niu",
  TOKELAUAN = "tkl",
  GREENLANDIC = "kl",
}

export const languageLabel: { [key in LanguageEnum]: string } = {
  [LanguageEnum.CHINESE]: "Chinese",
  [LanguageEnum.ENGLISH]: "English",
  [LanguageEnum.HINDI]: "Hindi",
  [LanguageEnum.SPANISH]: "Spanish",
  [LanguageEnum.ARABIC]: "Arabic",
  [LanguageEnum.FRENCH]: "French",
  [LanguageEnum.BENGALI]: "Bengali",
  [LanguageEnum.RUSSIAN]: "Russian",
  [LanguageEnum.PORTUGUESE]: "Portuguese",
  [LanguageEnum.URDU]: "Urdu",
  [LanguageEnum.INDONESIAN]: "Indonesian",
  [LanguageEnum.GERMAN]: "German",
  [LanguageEnum.JAPANESE]: "Japanese",
  [LanguageEnum.VIETNAMESE]: "Vietnamese",
  [LanguageEnum.KOREAN]: "Korean",
  [LanguageEnum.TAMIL]: "Tamil",
  [LanguageEnum.TURKISH]: "Turkish",
  [LanguageEnum.ITALIAN]: "Italian",
  [LanguageEnum.THAI]: "Thai",
  [LanguageEnum.PERSIAN]: "Persian",
  [LanguageEnum.POLISH]: "Polish",
  [LanguageEnum.UKRAINIAN]: "Ukrainian",
  [LanguageEnum.BURMESE]: "Burmese",
  [LanguageEnum.SWAHILI]: "Swahili",
  [LanguageEnum.PASHTO]: "Pashto",
  [LanguageEnum.AMHARIC]: "Amharic",
  [LanguageEnum.ROMANIAN]: "Romanian",
  [LanguageEnum.DUTCH]: "Dutch",
  [LanguageEnum.SINHALA]: "Sinhala",
  [LanguageEnum.SWEDISH]: "Swedish",
  [LanguageEnum.CZECH]: "Czech",
  [LanguageEnum.HUNGARIAN]: "Hungarian",
  [LanguageEnum.AZERBAIJANI]: "Azerbaijani",
  [LanguageEnum.GREEK]: "Greek",
  [LanguageEnum.HEBREW]: "Hebrew",
  [LanguageEnum.NEPALI]: "Nepali",
  [LanguageEnum.KHMER]: "Khmer",
  [LanguageEnum.ZULU]: "Zulu",
  [LanguageEnum.SOMALI]: "Somali",
  [LanguageEnum.FINNISH]: "Finnish",
  [LanguageEnum.BULGARIAN]: "Bulgarian",
  [LanguageEnum.SLOVAK]: "Slovak",
  [LanguageEnum.DANISH]: "Danish",
  [LanguageEnum.CATALAN]: "Catalan",
  [LanguageEnum.CROATIAN]: "Croatian",
  [LanguageEnum.DZONGKHA]: "Dzongkha",
  [LanguageEnum.ESTONIAN]: "Estonian",
  [LanguageEnum.GEORGIAN]: "Georgian",
  [LanguageEnum.ICELANDIC]: "Icelandic",
  [LanguageEnum.IRISH]: "Irish",
  [LanguageEnum.KAZAKH]: "Kazakh",
  [LanguageEnum.KURDISH]: "Kurdish",
  [LanguageEnum.KYRGYZ]: "Kyrgyz",
  [LanguageEnum.LAO]: "Lao",
  [LanguageEnum.LATIN]: "Latin",
  [LanguageEnum.LATVIAN]: "Latvian",
  [LanguageEnum.LITHUANIAN]: "Lithuanian",
  [LanguageEnum.MACEDONIAN]: "Macedonian",
  [LanguageEnum.MALAGASY]: "Malagasy",
  [LanguageEnum.MALAY]: "Malay",
  [LanguageEnum.MALTESE]: "Maltese",
  [LanguageEnum.MONGOLIAN]: "Mongolian",
  [LanguageEnum.NORWEGIAN]: "Norwegian",
  [LanguageEnum.SERBIAN]: "Serbian",
  [LanguageEnum.SLOVENIAN]: "Slovenian",
  [LanguageEnum.TAJIK]: "Tajik",
  [LanguageEnum.TURKMEN]: "Turkmen",
  [LanguageEnum.UZBEK]: "Uzbek",
  [LanguageEnum.XHOSA]: "Xhosa",
  [LanguageEnum.FILIPINO]: "Filipino",
  [LanguageEnum.DIVEHI]: "Divehi",
  [LanguageEnum.BELARUSIAN]: "Belarusian",
  [LanguageEnum.ARMENIAN]: "Armenian",
  [LanguageEnum.TIGRINYA]: "Tigrinya",
  [LanguageEnum.KINYARWANDA]: "Kinyarwanda",
  [LanguageEnum.KIRUNDI]: "Kirundi",
  [LanguageEnum.CAPE_VERDEAN_CREOLE]: "Cape Verdean Creole",
  [LanguageEnum.NYANJA]: "Nyanja",
  [LanguageEnum.SWATI]: "Swati",
  [LanguageEnum.COMORIAN]: "Comorian",
  [LanguageEnum.SOUTHERN_SOTHO]: "Southern Sotho",
  [LanguageEnum.HAITIAN_CREOLE]: "Haitian Creole",
  [LanguageEnum.LUXEMBOURGISH]: "Luxembourgish",
  [LanguageEnum.ALBANIAN]: "Albanian",
  [LanguageEnum.MONTENEGRIN]: "Montenegrin",
  [LanguageEnum.FAROESE]: "Faroese",
  [LanguageEnum.BOSNIAN]: "Bosnian",
  [LanguageEnum.PALAUAN]: "Palauan",
  [LanguageEnum.MARSHALLESE]: "Marshallese",
  [LanguageEnum.GILBERTESE]: "Gilbertese",
  [LanguageEnum.NAURU]: "Nauru",
  [LanguageEnum.BISLAMA]: "Bislama",
  [LanguageEnum.FIJIAN]: "Fijian",
  [LanguageEnum.TUVALUAN]: "Tuvaluan",
  [LanguageEnum.SAMOAN]: "Samoan",
  [LanguageEnum.TONGAN]: "Tongan",
  [LanguageEnum.RAROTONGAN]: "Rarotongan",
  [LanguageEnum.NIUEAN]: "Niuean",
  [LanguageEnum.TOKELAUAN]: "Tokelauan",
  [LanguageEnum.GREENLANDIC]: "Greenlandic",
};

export const selectLanguagesOptions = Object.entries(LanguageEnum)
  .map(([label, value]) => {
    return {
      label: languageLabel[value as LanguageEnum],
      value,
    };
  })
  ?.sort((a: { label: string }, b: { label: string }) =>
    a.label.localeCompare(b.label),
  );

export const getContentType = (orderDetail: any, t?: any, details?: any,) => {
  const contentType: string = orderDetail?.interestedPriceFields?.find(
    (el: any) => el.contentType,
  )?.contentType;
  const platform: string = orderDetail?.platform;
  let type = "";
  if (platform === PlatFormEnum.YOUTUBE) {
    if (contentType === "Dedicated video") {
      type = t
        ? t("campaignCreate.platformContentLabel.youtubeDedicated")
        : "YouTube Dedicated video";
    } else if (contentType === "Integrated video") {
      type = t
        ? t("campaignCreate.platformContentLabel.youtubeIntegrated")
        : "YouTube Integrated video";
    } else {
      const content = [];
      if (details?.placements?.includes(PlatformContentEnum.YOUTUBE_DEDICATED)) {
        content.push(t("campaignCreate.platformContentLabel.youtubeDedicated"));
      }
      if (details?.placements?.includes(PlatformContentEnum.YOUTUBE_INTEGRATED)) {
        content.push(t("campaignCreate.platformContentLabel.youtubeIntegrated"));
      }
      type = content.join(t ? t("campaignCreate.platformContentLabel.or") : " or ");
    }
  } else if (platform === PlatFormEnum.TIKTOK) {
    type = t
      ? t("campaignCreate.platformContentLabel.tiktokVideo")
      : "TikTok video";
  } else if (platform === PlatFormEnum.INSTAGRAM) {
    if (contentType === "Reels") {
      type = t
        ? t("campaignCreate.platformContentLabel.instagramReel")
        : "Instagram Reels";
    } else if (contentType === "Post") {
      type = t
        ? t("campaignCreate.platformContentLabel.instagramCarouselPost")
        : "Instagram carousel post";
    } else {
      const content = [];
      if (details?.placements?.includes(PlatformContentEnum.INSTAGRAM_REEL)) {
        content.push(t("campaignCreate.platformContentLabel.instagramReel"));
      }
      if (details?.placements?.includes(PlatformContentEnum.INSTAGRAM_CAROUSEL_POST)) {
        content.push(t("campaignCreate.platformContentLabel.instagramCarouselPost"));
      }
      type = content.join(t ? t("campaignCreate.platformContentLabel.or") : " or ");
    }
  } else if (platform === PlatFormEnum.TWITTER || platform === PlatFormEnum.X) {
    type = t ? t("campaignCreate.platformContentLabel.twitterPost") : "X post";
  } else if (platform === PlatFormEnum.LINKEDIN) {
    if (contentType?.includes("Image")) {
      type = t
        ? t("campaignCreate.platformContentLabel.linkedinImagePost")
        : "LinkedIn image post";
    } else if (contentType?.includes("Video")) {
      type = t
        ? t("campaignCreate.platformContentLabel.linkedinVideoPost")
        : "LinkedIn video post";
    } else {
      type = t
        ? t("campaignCreate.platformContentLabel.linkedinPost")
        : "LinkedIn post";
    }
  } else {
    type = t ? t("common.video", "Video") : "Video";
  }
  return type || "Video";
};

export enum ReginEnum {
  NORTH_AMERICA = "North America",
  LATIN_AMERICA = "Latin America",
  WESTERN_EUROPE = "Western Europe",
  EASTERN_EUROPE_AND_RUSSIA = "Eastern Europe & Russia",
  MIDDLE_EAST = "Middle East",
  SOUTH_ASIA = "South Asia",
  SOUTH_EAST_ASIA = "Southeast Asia",
  EAST_ASIA = "East Asia",
  OCEANIA = "Oceania",
  AFRICA = "Africa",
  Other = "Other",
}

export const areasOptions = Object.entries(ReginEnum).map(([label, value]) => {
  return {
    label: value,
    value,
  };
});

// 达人黑名单模版
export const SAMPLE_INFLUENCER_LIST_CSV_LINK =
  "https://aha-assets-public.s3.amazonaws.com/influencer-list-template.csv";

// post date enum
export enum PostTimeType {
  FLEXIBLE = "flexible",
  FIXED = "fixed",
}

export const DISABLED_TIME_LEN = 30;

// campaign.customerScaleType 代表客户类型， LARGE_SCALE 是大客户
export enum CustomerScaleTypeList {
  SMALL_SCALE = "small_scale",
  LARGE_SCALE = "large_scale",
}

export const isTwitterOrLinkedInOnly = (platforms: string[]) => {
  const twitterLinkedInPlatforms = [
    PlatformContentEnum.TWITTER_POST,
    PlatformContentEnum.LINKEDIN_POST,
    PlatformContentEnum.LINKEDIN_IMAGE_POST,
    PlatformContentEnum.LINKEDIN_VIDEO_POST,
  ];
  return (
    platforms.length > 0 &&
    platforms.every((platform) =>
      twitterLinkedInPlatforms?.includes(platform as PlatformContentEnum),
    )
  );
};
